import 'assets/vendors/style';
import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getToken } from 'util/authUtils';
import asyncComponent from 'util/asyncComponent';
import { bridgeSetTokens, forceReloadPage } from 'slices/BridgeSlice';
import qs from 'qs';
import SignIn from './SignIn';
import Embedded from './Embedded';
import * as actions from '../actions';
import { changeCompactMode, changeShop, changeApplication } from '../actions';
import AppLayout from './AppLayout';
import WizardShell from './WizardShell';
import {
  getInitUrlByShop,
  getShopIdFromLocation,
  appInIframe,
  getAdminProductId,
} from '../util/appUtils';
import AlertBanner from './AlertBanner';
import AppProvider from '../AppProvider';
import LoadingPage from './Embedded/LoadingPage';
import { canAccessApp } from './appAccess';
import { resolveEntryRedirect } from './appEntryRouting';
import { ROUTE_PATTERNS, ROUTES } from '../app/routes/routeMap';
import FacetsPrototype from '../features/facets/prototype/fixtureFacetsPrototype';

const App = () => {
  const history = useHistory();
  const location = useLocation();

  const dispatch = useDispatch();
  const { auth, app } = useSelector((store) => store, undefined);
  const { initURL, authorization } = auth;

  const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { mode, flow, appId } = queryString;

  const shopToken = getShopIdFromLocation();
  useEffect(() => {
    if (shopToken) {
      dispatch(changeShop(shopToken));
    }
  }, [shopToken, dispatch]);

  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    if (initURL === '') {
      dispatch(actions.setInitUrl(history.location.pathname));
    }
  }, [initURL]);

  const sessionToken = getToken();
  const productId = getAdminProductId();
  const embeddedProduct = productId === 'plugin';

  useEffect(() => {
    // A stale standalone cloud token may exist on the same origin as an
    // embedded plugin session. The plugin composition must never bootstrap
    // `/account` or reload itself because cloud authorization expired.
    if (sessionToken && !embeddedProduct) {
      dispatch(actions.getAuthorization());
    }
  }, [sessionToken, embeddedProduct, dispatch]);

  useEffect(() => {
    if (embeddedProduct && flow === 'login_plugin' && authorization.isFetched) {
      const theFirstShop = authorization.shopList[0];
      if (theFirstShop) {
        dispatch(bridgeSetTokens({
          shopId: theFirstShop.shopId,
          token: theFirstShop.token,
          secret: theFirstShop.secret,
        }));
      } else {
        forceReloadPage();
      }
    }
  }, [embeddedProduct, flow, authorization.isFetched, authorization.shopList, dispatch]);

  useEffect(() => {
    if (location.pathname === '/' && mode === 'embedded') {
      dispatch(changeCompactMode(true));
    }
  }, [location.pathname, mode]);

  useEffect(() => {
    if (location.pathname === '/' && appId) {
      dispatch(changeApplication(appId));
    }
  }, [location.pathname, appId]);

  const entryRedirect = resolveEntryRedirect({
    pathname: location.pathname,
    mode,
    flow,
    productId,
    authorizationFetched: authorization.isFetched,
    initURL,
    sessionToken,
  });
  if (entryRedirect === 'loading') return <LoadingPage />;
  if (entryRedirect) return <Redirect to={entryRedirect} />;

  if (location.pathname === '/app' && authorization.isFetched) {
    if (authorization.shopList.length) {
      const shopConfig = authorization.shopList[0];
      const initUrlForShop = getInitUrlByShop(shopConfig, app.currentApp);
      return <Redirect to={initUrlForShop} />;
    }
  }

  return (
    <AppProvider>
      <AlertBanner location={location} />
      <div className="app-main">
        <Switch>
          {/* Fixture-only review route. It never calls the plugin bridge or mutates a store. */}
          <Route path="/prototype/facets" component={FacetsPrototype} />

          {/* ── Auth shell ─────────────────────────────────────────────────
              Full-screen sign-in and password-recovery surface.
              No app nav. No wizard chrome. Outside the /app mount. */}
          <Route path={ROUTE_PATTERNS.signin} component={SignIn} />

          {/* ── Wizard shell ───────────────────────────────────────────────
              Fullscreen setup wizard. No top nav. Intercepted before
              the normal app mount so AppLayout is never rendered here. */}
          <RestrictedRoute
            path={[ROUTE_PATTERNS.setup, ROUTE_PATTERNS.searchSetup]}
            sessionToken={sessionToken}
            component={WizardShell}
          />

          {/* ── Normal app shell ───────────────────────────────────────────
              All /app/... pages rendered inside AppLayout (fixed top nav).
              ShellWizardGate inside AppLayout sends first-install iFrame
              users to /setup when the wizard is explicitly incomplete. */}
          <RestrictedRoute
            path={ROUTE_PATTERNS.app}
            sessionToken={sessionToken}
            component={AppLayout}
          />

          {/* ── Embedded / dev shell ───────────────────────────────────────
              Legacy embedded entry and /embedded/dev dev-only DevShell.
              Production navigation never links here. */}
          <Route path={ROUTE_PATTERNS.embedded} component={Embedded} />

          <Route component={asyncComponent(() => import('app/routes/404'))} />
        </Switch>
      </div>
    </AppProvider>
  );
};

export default App;

const RestrictedRoute = ({ component: Component, sessionToken, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      if (!canAccessApp({ sessionToken, inIframeStartMode: appInIframe() })) {
        return (
          <Redirect
            to={{
              pathname: ROUTES.signin(),
              state: { from: props.location },
            }}
          />
        );
      }

      return <Component {...props} />;
    }}
  />
);
